import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Grid } from '@material-ui/core';
import axios from 'axios';
import map from 'lodash/map';
import { CircleDiagram } from '../components/Charts/CirceDiagram';
import { BarChart } from '../components/Charts/Bar';
import { Heatmap } from '../components/Charts/Heatmap';
import { useAuth } from '../context/auth';
import { Overview } from './Sections/Overview';
import { Content } from './Sections/Content';
import { Locations } from './Sections/Locations';
import { Products } from './Sections/Products';
import { NoInformation } from './Sections/NoInformation';
import { Owner } from './Sections/Owner';
import { Header } from './Sections/Header';
import { Banner } from './Sections/Banner';
import { Status } from '../statuses';
import { theme } from '../theme';
import {
    InformationTitle,
    FlexWrapper,
    Numbers,
    ChartWrapper,
    InformationDescription
} from './CampaignDetails.styles';
import {
    ParamsProps,
    CampaignType,
    CampaignTypeApiResponse,
    CampaignStatisticApiResponse,
    CampaignStatisticType
} from './CampaignDetails.interface';

export function CampaignDetails() {
    const { id } = useParams<ParamsProps>();
    const { user, authToken } = useAuth();
    const [campaignData, setCampaignData] = useState<CampaignType>();
    const [campaignStatistic, setCampaignStatistic] = useState<CampaignStatisticType>();
    const [status, setStatus] = useState('');
    const [pollPreviewData, setPollPreviewData] = useState({});
    const [adminAccess, setAdminAccess] = useState<boolean>(false);
    const fetchData = () => {
        axios.get(`${process.env.PUBLIC_URL}/api/campaign/campaign/${id}/`, { headers: { DTOKEN: `${authToken}` } }).then(res => {
            const results = res.data as CampaignTypeApiResponse;
            setCampaignData({
                title: results.title,
                status: results.status,
                startDate: results.start_date,
                endDate: results.end_date,
                scheduleEndDate: results.schedule_end_date,
                specialities: results.specialities,
                adType: results.ad_type,
                owner: results.owner,
                details: {
                    redirectLink: results.details.redirect_link,
                    description: results.details.description,
                    mediaFile: results.details.media_file,
                    mobileFile: results.details.mobile_file,
                    desktopFile: results.details.desktop_file,
                    pollQuestion: results.details.poll_question,
                    postVotingMessage: results.details.post_voting_message,
                    answers: results.details.answers ? results.details.answers.map((answer: { id: number, question_answer: string }) => {
                        return {
                            id: answer.id,
                            questionAnswer: answer.question_answer
                        }
                    }) : undefined
                },
                products: results.products ? results.products.map((each: any) => {
                    return {
                        id: each.id,
                        productCode: each.product_code,
                        displayName: each.display_name,
                    }  
                }) : [],
                locations: results.locations,
                dovepoints: {
                    usedDovePoints: results.dovepoints.used_dovepoints,
                    dovePointsLimit: results.dovepoints.dovepoints_limit,
                },
                impressions: results.impressions,
                engagements: results.engagements,
                extraDate: results.extra_date,
                extraDescription: results.extra_description
            })
            setStatus(res.data.status);
            if (results.ad_type === 'Poll') {
                setPollPreviewData({
                    pollQuestion: results.details.poll_question,
                    postVotingMessage: results.details.post_voting_message,
                    answers: results.details.answers ? results.details.answers.map((answer: { id: number, question_answer: string }) => {
                        return {
                            id: answer.id,
                            questionAnswer: answer.question_answer
                        }
                    }) : undefined
                })
            }
        });
        axios.get(`${process.env.PUBLIC_URL}/api/campaign/campaign/${id}/statistic/`, { headers: { DTOKEN: `${authToken}` } }).then(res => {
            const results = res.data as CampaignStatisticApiResponse;
            setCampaignStatistic({
                impressionPerLocation: results.impressions_per_location,
                impressionPerProduct: results.impressions_per_product,
                engagementPerLocation: results.engagements_per_location,
                engagementPerProduct: results.engagements_per_product,
                impressionsPerClientType: results.impressions_per_client_type,
                engagementsPerClientType: results.engagements_per_client_type,
                heatMapImpressions: results.heat_map_impressions,
                heatMapEngagements: results.heat_map_engagements,
                pollResults: results.poll_results ? results.poll_results.map((answer: { id: number, question_answer: string, votes_counter: number }) => {
                    return {
                        id: answer.id,
                        questionAnswer: answer.question_answer,
                        votesCounter: answer.votes_counter
                    }
                }) : undefined,
                totalEngagements: results.total_engagements,
                totalImpressions: results.total_impressions
            });
        })
    }
    useEffect(() => {
        window.scrollTo(0, 0);
        fetchData();
        if (user) {
            setAdminAccess(user.isAdmin);
        }
    }, [user, id]);
    const chartCreateObject = (e: {}, color: string) => {
        const data = {
            labels: map(e, 'name'),
            datasets: [
                {
                    data: map(e, 'value'),
                    backgroundColor: color,
                    barThickness: 8
                }
            ]
        }
        return data;
    }
    // TODO: HeaderContainer mobile styles fix
    return (
        campaignData ?
            <>
                <Header
                     id={parseInt(id)}
                     title={campaignData.title}
                     status={campaignData.status}
                     adminAccess={adminAccess}
                     refresh={fetchData}
                     mediaFile={campaignData.details.mediaFile}
                     mobileFile={campaignData.details.mobileFile}
                     desktopFile={campaignData.details.desktopFile}
                     pollDetails={pollPreviewData}
                     adType={campaignData.adType}
                />
                <Container>
                    {(campaignData.extraDate && campaignData.extraDescription) &&
                        <Banner 
                            id={parseInt(id)}
                            extraDate={campaignData.extraDate}
                            extraDescription={campaignData.extraDescription}
                            status={status}
                            refresh={fetchData}
                        />
                    }
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            {adminAccess && <Owner user={campaignData.owner} status={status} campaignId={parseInt(id)} refresh={fetchData} />}
                            <Overview
                                status={campaignData.status}
                                startDate={campaignData.startDate}
                                endDate={campaignData.endDate}
                                dovepoints={campaignData.dovepoints}
                                scheduledEndDate={campaignData.scheduleEndDate}
                            />
                            <Content
                                adType={campaignData.adType}
                                details={campaignData.details}
                            />
                            <Locations locations={campaignData.locations} specialities={campaignData.specialities} />
                            <Products products={campaignData.products} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {campaignStatistic && (campaignStatistic.totalEngagements > 0 || campaignStatistic.totalImpressions > 0) && (status !== Status.WaitingForApproval && status !== Status.Draft && status !== Status.Scheduled && status !== Status.NotApproved) ?
                                <>
                                    {(campaignStatistic.totalImpressions > 0 || campaignStatistic.totalEngagements > 0) && 
                                        <FlexWrapper>
                                            <div>
                                                <InformationTitle>Impressions (Views)</InformationTitle>
                                                <Numbers>{campaignStatistic.totalImpressions}</Numbers>
                                            </div>
                                            <div>
                                                <InformationTitle>Engagements (Clicks)</InformationTitle>
                                                <Numbers>{campaignStatistic.totalEngagements}</Numbers>
                                            </div>
                                        </FlexWrapper>
                                    }
                                    {(campaignStatistic.pollResults && map(campaignStatistic.pollResults, 'votesCounter').reduce((sum, acc) => sum + acc) > 0) &&
                                        <ChartWrapper>
                                            <CircleDiagram
                                                diagramData={map(campaignStatistic.pollResults, 'votesCounter')}
                                                diagramColors={[
                                                    theme.chartsColors.orange,
                                                    theme.colors.primary,
                                                    theme.chartsColors.lightRed,
                                                    theme.chartsColors.mint
                                                ]}
                                                diagramLabels={map(campaignStatistic.pollResults, 'questionAnswer')}
                                                showVotes={true}
                                                title="Votes"
                                            />
                                        </ChartWrapper>
                                    }
                                    {campaignStatistic.totalImpressions > 0 && 
                                        <>
                                            <ChartWrapper>
                                                <BarChart data={chartCreateObject(campaignStatistic.impressionPerLocation, theme.colors.primary)} title="Impressions per location" />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <BarChart data={chartCreateObject(campaignStatistic.impressionPerProduct, theme.chartsColors.lightBlue)} title="Impressions per product" />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <CircleDiagram
                                                    diagramData={map(campaignStatistic.impressionsPerClientType, 'value')}
                                                    diagramColors={[
                                                        theme.chartsColors.orange,
                                                        theme.chartsColors.lightRed
                                                    ]}
                                                    diagramLabels={map(campaignStatistic.impressionsPerClientType, 'name')}
                                                    showVotes={false}
                                                    title="Impressions per client type"
                                                />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <InformationDescription><b>Impressions heatmap</b></InformationDescription>
                                                <Heatmap data={campaignStatistic.heatMapImpressions} name={"impressions"} />
                                            </ChartWrapper>
                                        </>
                                    }
                                    {campaignStatistic.totalEngagements > 0 && 
                                        <>
                                            <ChartWrapper>
                                                <BarChart data={chartCreateObject(campaignStatistic.engagementPerLocation, theme.chartsColors.orange)} title="Engagements per location" />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <BarChart data={chartCreateObject(campaignStatistic.engagementPerProduct, theme.chartsColors.mint)} title="Engagements per product" />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <CircleDiagram
                                                    diagramData={map(campaignStatistic.engagementsPerClientType, 'value')}
                                                    diagramColors={[
                                                        theme.chartsColors.lightRed,
                                                        theme.chartsColors.lightOrange
                                                    ]}
                                                    diagramLabels={map(campaignStatistic.engagementsPerClientType, 'name')}
                                                    showVotes={false}
                                                    title="Engagements per client type"
                                                />
                                            </ChartWrapper>
                                            <ChartWrapper>
                                                <InformationDescription><b>Engagements heatmap</b></InformationDescription>
                                                <Heatmap data={campaignStatistic.heatMapEngagements} name="engagements" />
                                            </ChartWrapper>
                                        </>
                                    }
                                </>
                                :
                                <NoInformation />
                            }
                        </Grid>
                    </Grid>
                </Container>  
            </>
            :
            <>
                <h1>loading</h1>
            </>
    )
}
